/**
 * How many columns to draw `panelCount` panels in, given the most that fit. Fewest rows wins, then
 * fewest empty cells in the last row, then more columns. Six panels in at most four columns come out
 * as 3 + 3 rather than 4 + 2; five come out as 3 + 2 rather than 4 + 1.
 *
 * @param panelCount - How many panels the grid holds.
 * @param maximumColumns - The most columns the current width and layout allow.
 * @returns The column count, between 1 and `maximumColumns` (never more than `panelCount`).
 */
export function columnsFor(panelCount: number, maximumColumns: number): number {
  if (panelCount <= 1 || maximumColumns <= 1) {
    return 1;
  }

  const widest = Math.min(maximumColumns, panelCount);
  let best = 1;
  let bestRows = Infinity;
  let bestEmptyCells = Infinity;
  for (let columns = 1; columns <= widest; columns += 1) {
    const rows = Math.ceil(panelCount / columns);
    const emptyCells = rows * columns - panelCount;
    const better =
      rows < bestRows ||
      (rows === bestRows && (emptyCells < bestEmptyCells || (emptyCells === bestEmptyCells && columns > best)));
    if (better) {
      best = columns;
      bestRows = rows;
      bestEmptyCells = emptyCells;
    }
  }

  return best;
}
